import React, { useState, useEffect, useCallback } from "react"
import { DollarSign, TrendingUp, Users, ShoppingCart, ArrowRight } from "lucide-react"
import { Card, CardContent } from "../../../components/ui/card"
import { TiltCard } from "../../../components/ui/TiltCard"
import RevenueTrendCard from "./RevenueTrendCard"
import RevenueByProductCard from "./RevenueByProductCard"
import { computeRevenueKPIs } from "../../../utils/dashboardAnalyticsHelpers"

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0)

const formatChange = (change) => {
  if (change === null || change === undefined) return null
  return `${change > 0 ? "+" : ""}${change}%`
}

const RevenueOverviewSection = ({ orgId, dateRange, onNavigateTab, showReportLink = true }) => {
  const [kpis, setKpis] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadKPIs = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    const data = await computeRevenueKPIs(orgId, dateRange)
    setKpis(data)
    setLoading(false)
  }, [orgId, dateRange])

  useEffect(() => {
    loadKPIs()
  }, [loadKPIs])

  const revenueKPIs = [
    { label: "Gross Revenue", value: formatCurrency(kpis?.grossRevenue?.total), change: formatChange(kpis?.grossRevenue?.change), icon: DollarSign },
    { label: "Net Revenue", value: formatCurrency(kpis?.netRevenue?.total), change: formatChange(kpis?.netRevenue?.change), icon: TrendingUp },
    { label: "Paid Customers", value: (kpis?.paidCustomers?.total ?? 0).toLocaleString(), change: formatChange(kpis?.paidCustomers?.change), icon: Users },
    { label: "Average Order Value", value: formatCurrency(kpis?.averageOrderValue?.total), change: formatChange(kpis?.averageOrderValue?.change), icon: ShoppingCart },
  ]

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-foreground">Revenue</h2>
        {showReportLink && (
          <button
            onClick={() => onNavigateTab?.("commerce-revenue")}
            className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            See Full Report
            <ArrowRight className="size-3.5" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {Array.from({ length: 4 })?.map((_, i) => (
            <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {revenueKPIs.map((kpi) => {
            const Icon = kpi.icon
            const isPositive = kpi.change && !kpi.change.startsWith("-")
            return (
              <TiltCard key={kpi.label} onClick={() => onNavigateTab?.("commerce-revenue")} tiltStrength={6}>
                <Card className="shadow-none h-full">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-2">
                      <Icon className="size-4" />
                      <span className="text-sm font-medium">{kpi.label}</span>
                    </div>
                    <p className="text-2xl font-semibold tracking-tight text-foreground">{kpi.value}</p>
                    {kpi.change && (
                      <p className={`text-xs font-medium mt-1 ${isPositive ? "text-green-600" : "text-destructive"}`}>
                        {kpi.change} vs prior period
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TiltCard>
            )
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <RevenueTrendCard orgId={orgId} onNavigateTab={onNavigateTab} />
        <RevenueByProductCard orgId={orgId} dateRange={dateRange} onNavigateTab={onNavigateTab} />
      </div>
    </div>
  )
}

export default RevenueOverviewSection